import React, { useEffect, useState } from "react";
import { Drawer, Empty, Skeleton, Tag, Timeline, Typography } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Error as ErrorMsg } from "../../../../commons/Messages/Messages";
import { useIsMobile } from "../../../../design-system/useMediaQuery";
import { auditTrail, errorText } from "./api";

const IGNORED = new Set(["updatedAt", "__v"]);

const short = value => {
  if (value === null || value === undefined) return "—";
  if (Array.isArray(value)) return `[${value.length}]`;
  if (typeof value === "object") return JSON.stringify(value).slice(0, 80);
  return String(value);
};

/** Top-level fields whose value changed between before and after. */
const changes = (before, after) => {
  const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
  const out = [];
  keys.forEach(key => {
    if (IGNORED.has(key)) return;
    const a = JSON.stringify(before?.[key] ?? null);
    const b = JSON.stringify(after?.[key] ?? null);
    if (a !== b) out.push({ key, before: before?.[key], after: after?.[key] });
  });
  return out;
};

const ACTION_COLORS = {
  create: "green",
  update: "blue",
  reverse: "red",
  close: "gray",
  file: "purple",
  reopen: "orange"
};

const AuditDrawer = ({ open, collection, id, title, onClose }) => {
  const { t } = useTranslation("accounting");
  const isMobile = useIsMobile();
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!open || !id) return;
    setItems(null);
    auditTrail(collection, id)
      .then(res => setItems(res.data.history || []))
      .catch(err => {
        ErrorMsg(errorText(err, t("Could not load the audit trail")));
        setItems([]);
      });
  }, [open, collection, id, t]);

  return (
    <Drawer
      rootClassName="acct-module"
      open={open}
      onClose={onClose}
      width={isMobile ? "100%" : 520}
      title={title || t("Audit trail")}
    >
      {items === null ? (
        <Skeleton active />
      ) : items.length === 0 ? (
        <Empty description={t("No changes recorded")} />
      ) : (
        <Timeline
          items={items.map(log => {
            const diff = log.before ? changes(log.before, log.after) : [];
            return {
              key: log._id,
              color: ACTION_COLORS[log.action] || "blue",
              children: (
                <div className="acct-wrap">
                  <div>
                    <Tag>{log.action}</Tag>
                    <Typography.Text type="secondary">
                      {dayjs(log.at).format("DD.MM.YYYY HH:mm")}
                    </Typography.Text>
                  </div>
                  {log.note ? <Typography.Text italic>{log.note}</Typography.Text> : null}
                  {log.before ? (
                    diff.length ? (
                      <ul style={{ margin: "4px 0 0", paddingInlineStart: 18 }}>
                        {diff.map(d => (
                          <li key={d.key}>
                            <Typography.Text code>{d.key}</Typography.Text>: {short(d.before)} →{" "}
                            {short(d.after)}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div>
                        <Typography.Text type="secondary">{t("No field changed")}</Typography.Text>
                      </div>
                    )
                  ) : (
                    <div>
                      <Typography.Text type="secondary">{t("Created")}</Typography.Text>
                    </div>
                  )}
                </div>
              )
            };
          })}
        />
      )}
    </Drawer>
  );
};

export default AuditDrawer;
